/**
 * Service requests in the portal (tracker item 45, phase 4): the Request
 * service form, the customer's own requests with their status and the office's
 * decline reason, and one request's detail. Works for a linked customer (a
 * property of theirs or a typed address) and for an unlinked but email-verified
 * login (a typed address and contact details). Mobile first: one column, 44px
 * controls, no tables.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PortalPage, PortalServiceRequest } from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import {
  MAX_PREFERRED_DATES,
  NEW_ADDRESS,
  PORTAL_REQUESTS_PATH,
  PORTAL_REQUEST_SERVICE_PATH,
  RequestFailure,
  SERVICE_REQUEST_STATUS_LABELS,
  ServiceRequestForm,
  TIME_OF_DAY_LABELS,
  describePortalFailure,
  describeRequestFailure,
  emptyServiceRequestForm,
  localToday,
  ownedProperties,
  portalAddress,
  portalDay,
  preferredTimesLabel,
  serviceRequestBody,
  serviceRequestProblems,
  serviceRequestStatusClass,
  serviceRequestStatusNote,
  usesTypedAddress,
  PortalFailure,
} from '../../utils/portal';
import { SERVICE_REQUEST_TIMES_OF_DAY } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { EmptyBlock, FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { PortalBundleRequests } from './PortalBundleRequests';

const backLink = 'inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline';

function where(request: Pick<PortalServiceRequest, 'property' | 'serviceAddress'>): string {
  return request.property ? portalAddress(request.property) : request.serviceAddress ?? '';
}

// ------------------------------------------------------------ request form

export function PortalRequestServicePage() {
  const { me, reportFailure } = usePortal();
  const api = usePortalApi();
  const linked = me.status === 'LINKED';
  const owned = ownedProperties(me.properties);
  const hasOwned = linked && owned.length > 0;
  const today = localToday();
  const initial = useCallback(
    () => emptyServiceRequestForm({
      place: owned.length === 1 ? owned[0].id : '',
      contactName: me.user.name || '',
      contactPhone: me.user.phone || '',
    }),
    // The defaults come from the account once; the customer's edits are never overwritten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me.user.id],
  );
  const [form, setForm] = useState<ServiceRequestForm>(initial);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [sent, setSent] = useState<PortalServiceRequest | null>(null);
  const confirmationRef = useRef<HTMLHeadingElement>(null);

  const loadItems = useCallback(() => api.listPortalRequestableItems(), [api]);
  const { data: items, loading, failure: loadFailure, reload } = usePortalLoad(loadItems, 'We could not load the list of services.');

  useEffect(() => { if (sent) confirmationRef.current?.focus(); }, [sent]);

  const problems = serviceRequestProblems(form, linked, today, hasOwned);
  const typed = usesTypedAddress(form, linked, hasOwned);
  const set = <K extends keyof ServiceRequestForm>(field: K, value: ServiceRequestForm[K]) => setForm((current) => ({ ...current, [field]: value }));
  const error = (field: keyof ServiceRequestForm) => (touched ? problems[field] : undefined);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (busy || Object.keys(problems).length > 0) return;
    setBusy(true);
    setFailure(null);
    try {
      setSent(await api.createPortalServiceRequest(serviceRequestBody(form, linked, hasOwned)));
    } catch (cause) {
      const described = describeRequestFailure(cause, { company: me.companyName });
      if (described.kind === 'session') reportFailure({ kind: 'session', message: described.message });
      setFailure(described);
    } finally {
      setBusy(false);
    }
  };

  const another = () => {
    setSent(null);
    setTouched(false);
    setFailure(null);
    setForm(initial());
  };

  if (loading && !items) return <LoadingBlock label="Loading services" />;
  if (loadFailure) {
    return (
      <div className="space-y-3">
        <FailureBlock failure={loadFailure} onRetry={loadFailure.kind === 'not-found' ? undefined : () => void reload()} />
        <Link to="/portal" className={backLink}>Back to home</Link>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="space-y-4" data-testid="request-sent">
        <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" aria-labelledby="sent-heading">
          <h1 id="sent-heading" ref={confirmationRef} tabIndex={-1} className="text-lg font-bold text-gray-900">Request sent</h1>
          <p className="mt-2 text-sm text-gray-700">
            Thank you. {me.companyName} has your request and will reply here and by email at {me.user.email}.
          </p>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {sent.requestedItem && <><dt className="text-gray-600">Service</dt><dd className="break-words">{sent.requestedItem}</dd></>}
            <dt className="text-gray-600">Where</dt><dd className="break-words">{where(sent)}</dd>
            {preferredTimesLabel(sent) && <><dt className="text-gray-600">When</dt><dd className="break-words">{preferredTimesLabel(sent)}</dd></>}
          </dl>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Link to={`${PORTAL_REQUESTS_PATH}/${sent.id}`} className={`${primaryButtonClass} inline-flex min-h-[44px] items-center justify-center`}>View this request</Link>
            <button type="button" className={secondaryButtonClass} onClick={another}>Send another request</button>
            <Link to="/portal" className={`${secondaryButtonClass} inline-flex min-h-[44px] items-center justify-center`}>Back to home</Link>
          </div>
        </section>
      </div>
    );
  }

  const offered = items ?? [];
  return (
    <div className="space-y-4">
    <form onSubmit={(e) => void submit(e)} noValidate className="space-y-4" aria-labelledby="request-title">
      <div>
        <Link to="/portal" className={backLink}>&larr; Home</Link>
        <h1 id="request-title" className="text-lg font-bold text-gray-900">Request service</h1>
        <p className="mt-1 text-sm text-gray-600">
          Tell {me.companyName} what you need. This is a request, not a booking: we will reply with a time.
          {!linked && ' Your account is still being matched, but you can ask now.'}
        </p>
      </div>

      {failure && <InlineAlert>{failure.message}</InlineAlert>}

      <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div>
          <label htmlFor="rq-item" className={labelClass}>What do you need?</label>
          <select id="rq-item" className={`${inputClass} min-h-[44px]`} value={form.itemId} onChange={(e) => set('itemId', e.target.value)} disabled={busy}>
            <option value="">{offered.length ? 'Something else (describe it below)' : 'Describe what you need below'}</option>
            {offered.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          {offered.find((item) => item.id === form.itemId)?.description && (
            <p className="mt-1 break-words text-xs text-gray-600">{offered.find((item) => item.id === form.itemId)?.description}</p>
          )}
        </div>

        {hasOwned ? (
          <div>
            <label htmlFor="rq-place" className={labelClass}>Where?</label>
            <select
              id="rq-place" className={`${inputClass} min-h-[44px]`} value={form.place} onChange={(e) => set('place', e.target.value)} disabled={busy}
              aria-invalid={!!error('place')} aria-describedby={error('place') ? 'rq-place-error' : undefined}
            >
              {owned.length > 1 && <option value="">Choose a property</option>}
              {owned.map((p) => <option key={p.id} value={p.id}>{portalAddress(p)}</option>)}
              <option value={NEW_ADDRESS}>A different address</option>
            </select>
            {error('place') && <p id="rq-place-error" className="mt-1 text-xs text-red-700">{error('place')}</p>}
          </div>
        ) : null}
        {typed && (
          <div>
            <label htmlFor="rq-address" className={labelClass}>{hasOwned ? 'Address' : 'Where do you need the service?'}</label>
            <input
              id="rq-address" className={`${inputClass} min-h-[44px]`} autoComplete="street-address" maxLength={300} value={form.address}
              onChange={(e) => set('address', e.target.value)} disabled={busy} placeholder="Street, city, state, zip"
              aria-invalid={!!error('address')} aria-describedby={error('address') ? 'rq-address-error' : undefined}
            />
            {error('address') && <p id="rq-address-error" className="mt-1 text-xs text-red-700">{error('address')}</p>}
          </div>
        )}

        <fieldset>
          <legend className={labelClass}>Dates that suit you (optional, up to {MAX_PREFERRED_DATES})</legend>
          <div className="space-y-2">
            {form.dates.map((date, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="date" className={`${inputClass} min-h-[44px]`} min={today} value={date} disabled={busy}
                  aria-label={`Preferred date ${index + 1}`}
                  onChange={(e) => set('dates', form.dates.map((d, i) => (i === index ? e.target.value : d)))}
                />
                {form.dates.length > 1 && (
                  <button type="button" className={`${secondaryButtonClass} shrink-0`} disabled={busy} aria-label={`Remove date ${index + 1}`}
                    onClick={() => set('dates', form.dates.filter((_, i) => i !== index))}>Remove</button>
                )}
              </div>
            ))}
          </div>
          {error('dates') && <p className="mt-1 text-xs text-red-700">{error('dates')}</p>}
          {form.dates.length < MAX_PREFERRED_DATES && (
            <button type="button" className={`${secondaryButtonClass} mt-2`} disabled={busy} onClick={() => set('dates', [...form.dates, ''])}>Add another date</button>
          )}
        </fieldset>

        <div>
          <label htmlFor="rq-time" className={labelClass}>Time of day (optional)</label>
          <select id="rq-time" className={`${inputClass} min-h-[44px]`} value={form.timeOfDay} onChange={(e) => set('timeOfDay', e.target.value as ServiceRequestForm['timeOfDay'])} disabled={busy}>
            <option value="">No preference</option>
            {SERVICE_REQUEST_TIMES_OF_DAY.map((t) => <option key={t} value={t}>{TIME_OF_DAY_LABELS[t]}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="rq-notes" className={labelClass}>Notes{form.itemId ? ' (optional)' : ''}</label>
          <textarea
            id="rq-notes" className={inputClass} rows={4} maxLength={2000} value={form.notes} onChange={(e) => set('notes', e.target.value)} disabled={busy}
            aria-invalid={!!error('notes')} aria-describedby={error('notes') ? 'rq-notes-error' : undefined}
          />
          {error('notes') && <p id="rq-notes-error" className="mt-1 text-xs text-red-700">{error('notes')}</p>}
        </div>
      </section>

      {!linked && (
        <section className="space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" aria-labelledby="contact-heading">
          <h2 id="contact-heading" className="text-sm font-semibold text-gray-600">How can we reach you?</h2>
          <p className="text-xs text-gray-600">We will reply to {me.user.email}.</p>
          <div>
            <label htmlFor="rq-name" className={labelClass}>Your name</label>
            <input
              id="rq-name" className={`${inputClass} min-h-[44px]`} autoComplete="name" maxLength={200} value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)} disabled={busy}
              aria-invalid={!!error('contactName')} aria-describedby={error('contactName') ? 'rq-name-error' : undefined}
            />
            {error('contactName') && <p id="rq-name-error" className="mt-1 text-xs text-red-700">{error('contactName')}</p>}
          </div>
          <div>
            <label htmlFor="rq-phone" className={labelClass}>Phone (optional)</label>
            <input
              id="rq-phone" type="tel" className={`${inputClass} min-h-[44px]`} autoComplete="tel" maxLength={50} value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)} disabled={busy}
              aria-invalid={!!error('contactPhone')} aria-describedby={error('contactPhone') ? 'rq-phone-error' : undefined}
            />
            {error('contactPhone') && <p id="rq-phone-error" className="mt-1 text-xs text-red-700">{error('contactPhone')}</p>}
          </div>
        </section>
      )}

      <button type="submit" className={`${primaryButtonClass} w-full text-base sm:w-auto`} disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
    </form>
    <PortalBundleRequests />
    </div>
  );
}

// -------------------------------------------------------------- the list

const PAGE_SIZE = 20;

function RequestRow({ request }: { request: PortalServiceRequest }) {
  return (
    <li>
      <Link
        to={`${PORTAL_REQUESTS_PATH}/${request.id}`}
        className="block min-h-[44px] rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 hover:ring-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="request-row"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="min-w-0 break-words text-base font-semibold text-gray-900">{request.requestedItem ?? 'Service request'}</span>
          <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${serviceRequestStatusClass(request.status)}`}>{SERVICE_REQUEST_STATUS_LABELS[request.status]}</span>
        </div>
        <div className="mt-1 break-words text-sm text-gray-600">{where(request)}</div>
        <div className="mt-1 text-xs text-gray-500">Sent {portalDay(request.createdAt.slice(0, 10))}</div>
        {request.status === 'DECLINED' && request.declineReason && (
          <p className="mt-2 break-words rounded-md bg-red-50 p-2 text-sm text-red-800" data-testid="decline-reason">Reason: {request.declineReason}</p>
        )}
      </Link>
    </li>
  );
}

export function PortalRequestsPage() {
  const { reportFailure } = usePortal();
  const api = usePortalApi();
  const [rows, setRows] = useState<PortalServiceRequest[]>([]);
  const [page, setPage] = useState<PortalPage<PortalServiceRequest>['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [failure, setFailure] = useState<PortalFailure | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async (pageNo: number) => {
    const id = ++requestId.current;
    if (pageNo === 1) { setLoading(true); setRows([]); } else setMoreLoading(true);
    setFailure(null);
    try {
      const result = await api.listPortalServiceRequests({ page: pageNo, limit: PAGE_SIZE });
      if (id !== requestId.current) return;
      setRows((current) => (pageNo === 1 ? result.data : [...current, ...result.data]));
      setPage(result.pagination);
    } catch (error) {
      if (id !== requestId.current) return;
      const described = describePortalFailure(error, 'We could not load your requests.');
      setFailure(described);
      reportFailure(described);
    } finally {
      if (id === requestId.current) { setLoading(false); setMoreLoading(false); }
    }
  }, [api, reportFailure]);

  useEffect(() => { void load(1); }, [load]);

  const hasMore = !!page && page.page < page.pages;
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-gray-900">Your requests</h1>
        <Link to={PORTAL_REQUEST_SERVICE_PATH} className={`${primaryButtonClass} inline-flex min-h-[44px] items-center justify-center`}>Request service</Link>
      </div>
      {loading && <LoadingBlock label="Loading requests" />}
      {failure && <FailureBlock failure={failure} onRetry={() => void load(page && rows.length ? page.page + 1 : 1)} />}
      {!loading && !failure && rows.length === 0 && <EmptyBlock>You have not asked for any service yet.</EmptyBlock>}
      {rows.length > 0 && (
        <ul className="space-y-3" aria-label="Service requests">
          {rows.map((request) => <RequestRow key={request.id} request={request} />)}
        </ul>
      )}
      {hasMore && !failure && (
        <button type="button" className={`${secondaryButtonClass} mt-4 w-full`} disabled={moreLoading} onClick={() => void load((page?.page ?? 1) + 1)}>
          {moreLoading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------- the detail

export function PortalRequestPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { me } = usePortal();
  const api = usePortalApi();
  const load = useCallback(() => api.getPortalServiceRequest(id), [api, id]);
  const { data: request, loading, failure, reload } = usePortalLoad(load, 'We could not load this request.', id);

  if (loading && !request) return <LoadingBlock label="Loading request" />;
  if (failure) {
    return (
      <div className="space-y-3">
        <FailureBlock failure={failure} onRetry={failure.kind === 'not-found' ? undefined : () => void reload()} />
        <Link to={PORTAL_REQUESTS_PATH} className={backLink}>Back to your requests</Link>
      </div>
    );
  }
  if (!request) return null;

  return (
    <div className="space-y-4">
      <Link to={PORTAL_REQUESTS_PATH} className={backLink}>&larr; Requests</Link>
      <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" aria-labelledby="request-detail-heading">
        <div className="flex items-start justify-between gap-3">
          <h1 id="request-detail-heading" className="min-w-0 break-words text-lg font-bold text-gray-900">{request.requestedItem ?? 'Service request'}</h1>
          <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${serviceRequestStatusClass(request.status)}`}>{SERVICE_REQUEST_STATUS_LABELS[request.status]}</span>
        </div>
        <p
          className={`mt-3 rounded-md p-3 text-sm ${request.status === 'DECLINED' ? 'bg-red-50 text-red-800' : request.status === 'CONVERTED' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-900'}`}
          data-testid="request-status-note"
        >
          {serviceRequestStatusNote(request, me.companyName)}
        </p>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-gray-600">Where</dt><dd className="break-words" data-testid="request-where">{where(request)}</dd>
          {preferredTimesLabel(request) && <><dt className="text-gray-600">When</dt><dd className="break-words">{preferredTimesLabel(request)}</dd></>}
          {request.notes && <><dt className="text-gray-600">Notes</dt><dd className="whitespace-pre-wrap break-words">{request.notes}</dd></>}
          <dt className="text-gray-600">Sent</dt><dd>{portalDay(request.createdAt.slice(0, 10))}</dd>
          {request.decidedAt && <><dt className="text-gray-600">Answered</dt><dd>{portalDay(request.decidedAt.slice(0, 10))}</dd></>}
        </dl>
      </section>
    </div>
  );
}
